import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Grid } from "@material-ui/core";
import axios from "../../axios-order";
import ValidatedDatePicker from "../../components/ui/monthWiseDatePicker";
import BoxWrapper from "../../components/ui/boxWrapper";
import "../../HOC/layout/layout.css";
import { AddCircle, Close as CloseIcon } from "@mui/icons-material";
import Inputfield from "../../components/ui/inputField";
import Modal from "@mui/material/Modal";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import debounce from "lodash/debounce";
import { SSE_BASE_URL } from "../../axios-order";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";


const ISD_COLUMNS = [
  { field: "unit_type", headerName: "Unit Type", width: 130 },
  { field: "gstin_of_recipient", headerName: "GSTIN of Recipient", width: 200 },
  { field: "original_document_number", headerName: "Original Document Number", width: 200 },
  { field: "original_document_date", headerName: "Original Document Date", width: 180 },
  { field: "eligibility_of_itc", headerName: "Eligibility of ITC", width: 160 },
  { field: "unit_type_amended", headerName: "Unit Type (Amended)", width: 160 },
  { field: "gstin_of_recipient_amended", headerName: "GSTIN of Recipient (Amended)", width: 210 },
  { field: "isd_document_type", headerName: "ISD Document Type", width: 160 },
  { field: "isd_invoice_number", headerName: "ISD Invoice Number", width: 180 },
  { field: "isd_invoice_date", headerName: "ISD Invoice Date", width: 160 },
  { field: "isd_credit_note_number", headerName: "ISD Credit Note Number", width: 190 },
  { field: "isd_credit_note_date", headerName: "ISD Credit Note Date", width: 180 },
  { field: "original_invoice_number", headerName: "Original Invoice Number", width: 190 },
  { field: "original_invoice_date", headerName: "Original Invoice Date", width: 180 },
  { field: "taxable_value", headerName: "Taxable Value (₹)", width: 150 },
  { field: "is_unit_located_in_sez", headerName: "Unit in SEZ?", width: 130 },
  { field: "invoice_integrated_tax", headerName: "Invoice IGST (₹)", width: 150 },
  { field: "invoice_central_tax", headerName: "Invoice CGST (₹)", width: 150 },
  { field: "invoice_state_ut_tax", headerName: "Invoice SGST/UTGST (₹)", width: 170 },
  { field: "invoice_cess", headerName: "Invoice Cess (₹)", width: 140 },
  { field: "credit_note_integrated_tax", headerName: "Credit Note IGST (₹)", width: 170 },
  { field: "credit_note_central_tax", headerName: "Credit Note CGST (₹)", width: 170 },
  { field: "credit_note_state_ut_tax", headerName: "Credit Note SGST/UTGST (₹)", width: 190 },
  { field: "credit_note_cess", headerName: "Credit Note Cess (₹)", width: 160 },
  { field: "action", headerName: "Action", width: 100 },
];

const RECON_COLUMNS = [
  { field: "invoice_type", headerName: "Invoice Type", width: 130 },
  { field: "org_gstin", headerName: "GSTIN of supplier/ECO", width: 180 },
  { field: "name", headerName: "Trade/Legal name of the supplier/ECO", width: 220 },
  { field: "original_inv_no", headerName: "Original Invoice Number", width: 180 },
  { field: "original_inv_date", headerName: "Original Invoice Date", width: 160 },
  { field: "gst_type", headerName: "GST Type", width: 100 },
  { field: "invoice_no", headerName: "Invoice/Credit/Debit note number", width: 200 },
  { field: "invoice_date", headerName: "Invoice/Credit/Debit note Date", width: 180 },
  { field: "invoice_value", headerName: "Invoice Value (₹)", width: 150 },
  {
    field: "place_of_supply", headerName: "Place of supply", width: 140,
    type: "singleSelect", valueOptions: ["Inter-state", "Intra-state"], editable: true,
  },
  { field: "rate", headerName: "Rate(%)", width: 90 },
  { field: "tax_value", headerName: "Taxable value (₹)", width: 150 },
  { field: "integrated_tax", headerName: "Integrated tax (₹)", width: 150 },
  { field: "central_tax", headerName: "Central tax (₹)", width: 140 },
  { field: "state_tax", headerName: "State/UT tax (₹)", width: 150 },
  { field: "cess", headerName: "Cess (₹)", width: 110 },
  { field: "remarks", headerName: "Remarks As per GSTR-6 RECON", width: 220 },
  { field: "remarks_2", headerName: "Remarks 2", width: 150 },
  { field: "itc_eligibilty", headerName: "ITC eligibility", width: 140 },
  {
    field: "isd_applicable", headerName: "ISD Applicable", width: 130,
    editable: true, type: "singleSelect", valueOptions: ["Yes", "No"],
  },
  { field: "table_type", headerName: "GSTR-6 table type", width: 150 },
];

const derivePoS = (vendorGstin, customerGstin) => {
  if (!vendorGstin || !customerGstin) return "";
  return vendorGstin.substring(0, 2) === customerGstin.substring(0, 2) ? "Intra-State" : "Inter-State";
};

const downloadBase64Excel = (contentType, base64Data, fileName) => {
  const linkSource = `data:${contentType};base64,${base64Data}`;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

const fmt = (n) =>
  Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ─── Coloured Recon Popup ─────────────────────────────────────────── */
/* ─── Coloured Recon Popup ─────────────────────────────────────────── */
// ── NEW: reusable multi-select filter dropdown (checkbox list) ──
// const MultiSelectFilterDropdown = ({ options, selected, onChange, placeholder = "All" }) => {
//   const [open, setOpen] = useState(false);
//   const ref = useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (ref.current && !ref.current.contains(e.target)) setOpen(false);
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const toggleOption = (opt) => {
//     if (selected.includes(opt)) {
//       onChange(selected.filter((s) => s !== opt));
//     } else {
//       onChange([...selected, opt]);
//     }
//   };

//   const label = selected.length === 0
//     ? placeholder
//     : selected.length === 1
//       ? selected[0]
//       : `${selected.length} selected`;

//   return (
//     <Box ref={ref} sx={{ position: "relative" }}>
//       <button
//         type="button"
//         onClick={() => setOpen((o) => !o)}
//         style={{
//           width: "100%", fontSize: 11, padding: "3px 6px",
//           border: "1px solid #D3D1C7", borderRadius: 6,
//           background: selected.length > 0 ? "#EEEDFE" : "#fff",
//           color: "#2C2C2A", cursor: "pointer",
//           display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4,
//         }}
//       >
//         <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
//           {label}
//         </span>
//         <span style={{ fontSize: 9 }}>▾</span>
//       </button>

//       {open && (
//         <Box sx={{
//           position: "absolute", top: "100%", left: 0, mt: 0.5, zIndex: 30,
//           background: "#fff", border: "1px solid #D3D1C7", borderRadius: 1.5,
//           boxShadow: "0 4px 12px rgba(0,0,0,0.15)", minWidth: 170, maxHeight: 220, overflowY: "auto",
//         }}>
//           {options.length === 0 ? (
//             <Box sx={{ px: 1.5, py: 1, fontSize: 11, color: "#999" }}>No options</Box>
//           ) : (
//             options.map((opt) => (
//               <Box
//                 key={opt}
//                 onClick={() => toggleOption(opt)}
//                 sx={{
//                   display: "flex", alignItems: "center", gap: 1,
//                   px: 1.2, py: 0.6, fontSize: 11.5, cursor: "pointer",
//                   "&:hover": { background: "#F4F2FE" },
//                 }}
//               >
//                 <input
//                   type="checkbox"
//                   checked={selected.includes(opt)}
//                   readOnly
//                   style={{ width: 12, height: 12, accentColor: "#534AB7", cursor: "pointer" }}
//                 />
//                 <span>{opt}</span>
//               </Box>
//             ))
//           )}
//           {selected.length > 0 && (
//             <Box
//               onClick={() => onChange([])}
//               sx={{
//                 px: 1.2, py: 0.7, fontSize: 11, color: "#A63020", cursor: "pointer",
//                 borderTop: "1px solid #eee", textAlign: "center",
//               }}
//             >
//               Clear
//             </Box>
//           )}
//         </Box>
//       )}
//     </Box>
//   );
// };

/* ─── Coloured Recon Popup ─────────────────────────────────────────── */
// ── multi-select filter dropdown (checkbox list) ──
const MultiSelectFilterDropdown = ({ options, selected, onChange, placeholder = "All", align = "left" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
    setOpen(false); // FIX: close the dropdown right after a value is checked
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", fontSize: 11, padding: "3px 6px",
          border: "1px solid #D3D1C7", borderRadius: 6,
          background: selected.length > 0 ? "#EEEDFE" : "#fff",
          color: "#2C2C2A", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <span style={{ fontSize: 9 }}>▾</span>
      </button>

      {open && (
        <Box sx={{
          position: "absolute", top: "100%",
          ...(align === "right" ? { right: 0 } : { left: 0 }), // FIX: keep panel inside table for right-edge columns (e.g. "Table")
          mt: 0.5, zIndex: 30,
          background: "#fff", border: "1px solid #D3D1C7", borderRadius: 1.5,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)", minWidth: 170, maxHeight: 220, overflowY: "auto",
        }}>
          {options.length === 0 ? (
            <Box sx={{ px: 1.5, py: 1, fontSize: 11, color: "#999" }}>No options</Box>
          ) : (
            options.map((opt) => (
              <Box
                key={opt}
                onClick={() => toggleOption(opt)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.2, py: 0.6, fontSize: 11.5, cursor: "pointer",
                  "&:hover": { background: "#F4F2FE" },
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  readOnly
                  style={{ width: 12, height: 12, accentColor: "#534AB7", cursor: "pointer" }}
                />
                <span>{opt}</span>
              </Box>
            ))
          )}
          {selected.length > 0 && (
            <Box
              onClick={() => onChange([])}
              sx={{
                px: 1.2, py: 0.7, fontSize: 11, color: "#A63020", cursor: "pointer",
                borderTop: "1px solid #eee", textAlign: "center",
              }}
            >
              Clear
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ReconPopupDialog = ({ open, onClose, data, rowCount, reconId, onSubmit, onFilesAdded }) => {
  const [search, setSearch] = useState("");

  // ── Pagination ──
  const [rows, setRows] = useState(data);
  const [page, setPage] = useState(0); // 0-indexed
  const [total, setTotal] = useState(rowCount);
  const [pageLoading, setPageLoading] = useState(false);
  const pageSize = data?.length || 100; // matches whatever size backend sends per page

  const [selected, setSelected] = useState(
    new Set(data.filter((r) => r.selected === true).map((r) => r.id))
  );

  // ── Local editable copy of rows + tracking which rows' ISD was touched ──
  const [localRows, setLocalRows] = useState(data);
  const [modifiedIsd, setModifiedIsd] = useState(new Set());
  const [uploadLogs, setUploadLogs] = useState([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  // ── per-column filters. select-type -> array of values, text-type -> string ──
  const [columnFilters, setColumnFilters] = useState({});

  const [loadingAllRecords, setLoadingAllRecords] = useState(false);
  const [allRecordsMode, setAllRecordsMode] = useState(false);
  // ── Ref for the hidden "Upload Template" file input ──
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocalRows(data);
    setSelected(new Set(data.filter((r) => r.selected === true).map((r) => r.id)));
    setModifiedIsd(new Set());
  }, [data, open]);

  // ── NEW: debounced backend search for text-type filter columns ──
const textSearchDebounceRef = useRef(null);

useEffect(() => {
  const textKeys = ["org_gstin", "name", "invoice_no", "invoice_date"];
  const combined = textKeys
    .map((k) => columnFilters[k])
    .filter((v) => typeof v === "string" && v.trim() !== "")
    .join(" ")
    .trim();

  if (!combined || !reconId) return;

  if (textSearchDebounceRef.current) clearTimeout(textSearchDebounceRef.current);

  textSearchDebounceRef.current = setTimeout(async () => {
    try {
      const { data: res } = await axios.get(
        `/get_gstr6_search/${reconId}/${encodeURIComponent(combined)}`
      );
      if (res.status) {
        setLocalRows(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, 500);

  return () => clearTimeout(textSearchDebounceRef.current);
}, [columnFilters.org_gstin, columnFilters.name, columnFilters.invoice_no, columnFilters.invoice_date, reconId]);

  const handleIsdChange = (id, value) => {
    setLocalRows((prev) => prev.map((r) => (r.id === id ? { ...r, isd_applicable: value } : r)));
    setModifiedIsd((prev) => new Set(prev).add(id));
  };

  useEffect(() => {
    setRows(data);
    setTotal(rowCount);
    setPage(0);
    setAllRecordsMode(false);
  }, [data, rowCount, open]);

  // ── reset filters whenever dialog reopens / data changes ──
  useEffect(() => {
    setColumnFilters({});
  }, [data, open]);

  const totalPages = Math.ceil(total / pageSize);

  const fetchPage = async (nextPage) => {
    if (!reconId) return;
    try {
      setPageLoading(true);
      const { data: res } = await axios.get(`/get_gstr6_recon_for_filing/${reconId}`, {
        params: { page: nextPage + 1 },
      });
      if (!res.status) throw new Error(res.error || "Failed to load page");
      setRows(res.data);
      setLocalRows(res.data);
      setTotal(res.total);
      setPage(nextPage);
    } catch (err) {
      console.error(err);
    } finally {
      setPageLoading(false);
    }
  };

  const handleFetchAllRecords = async () => {
    if (!reconId) return;

    // If already showing all records, toggle back to normal pagination
    if (allRecordsMode) {
      setAllRecordsMode(false);
      await fetchPage(0);
      return;
    }

    try {
      setLoadingAllRecords(true);
      const { data: res } = await axios.post(`/get_all_records`, {
        recon_id: reconId,
        all_records: true,
      });
      if (!res.status) throw new Error(res.error || "Failed to load all records");
      setRows(res.data);
      setLocalRows(res.data);
      setTotal(res.total);
      setPage(0);
      setAllRecordsMode(true);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load all records");
    } finally {
      setLoadingAllRecords(false);
    }
  };

  // ── column definitions for filtering (key -> row field + filter type) ──
  const filterColumns = [
    { key: "invoice_type", field: "invoice_type", type: "select", label: "Type" },
    { key: "org_gstin", field: "org_gstin", type: "text", label: "GSTIN" },
    { key: "name", field: "name", type: "text", label: "Supplier name" },
    { key: "invoice_no", field: "invoice_no", type: "text", label: "Invoice no" },
    { key: "invoice_date", field: "invoice_date", type: "text", label: "Date" },
    { key: "remarks", field: "remarks", type: "select", label: "Remarks" },
    { key: "isd_applicable", field: "isd_applicable", type: "select", label: "ISD" },
    { key: "table_type", field: "table_type", type: "select", label: "Table" },
  ];

  // ── unique value options for select-type filters, derived from current rows ──
  const filterOptions = useMemo(() => {
    const opts = {};
    filterColumns.forEach((col) => {
      if (col.type === "select") {
        opts[col.key] = Array.from(
          new Set(
            localRows
              .map((r) => r[col.field])
              .filter((v) => v !== null && v !== undefined && v !== "")
          )
        );
      }
    });
    return opts;
  }, [localRows]);

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearColumnFilters = () => setColumnFilters({});

  const isFilterActive = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));

  // ── apply search + column filters (multi-select aware) together ──
  const filtered = useMemo(() => {
    let result = search
      ? localRows.filter(
        (r) =>
          r.invoice_no?.toLowerCase().includes(search.toLowerCase()) ||
          r.name?.toLowerCase().includes(search.toLowerCase())
      )
      : localRows;

    Object.entries(columnFilters).forEach(([key, value]) => {
      if (!isFilterActive(value)) return;
      const col = filterColumns.find((c) => c.key === key);
      if (!col) return;
      if (col.type === "select") {
        result = result.filter((r) => value.includes(String(r[col.field] ?? "")));
      } else {
        result = result.filter((r) =>
          String(r[col.field] ?? "").toLowerCase().includes(String(value).toLowerCase())
        );
      }
    });

    return result;
  }, [localRows, search, columnFilters]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(filtered.map((r) => r.id)));
    else setSelected(new Set());
  };

  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));

  const pillStyle = (type) => {
    const map = {
      INVOICE: { background: "#EEEDFE", color: "#3C3489" },
      yes: { background: "#C0DD97", color: "#173404" },
      no: { background: "#F5C4B3", color: "#4A1B0C" },
      match: { background: "#9FE1CB", color: "#04342C" },
      table: { background: "#B5D4F4", color: "#042C53" },
    };
    return {
      display: "inline-block", padding: "2px 8px", borderRadius: 20,
      fontSize: 11, fontWeight: 500, whiteSpace: "nowrap",
      ...(map[type] || { background: "#F1EFE8", color: "#2C2C2A" }),
    };
  };

  const thBase = {
    padding: "9px 10px", textAlign: "left", fontWeight: 500,
    fontSize: 12, whiteSpace: "nowrap",
    borderBottom: "0.5px solid #D3D1C7",
  };

  const cols = [
    { key: "type", label: "Type", group: "supplier", bg: "#EEEDFE", color: "#26215C" },
    { key: "gstin", label: "GSTIN", group: "supplier", bg: "#F4F2FE", color: "#26215C" },
    { key: "name", label: "Supplier name", group: "supplier", bg: "#F9F8FF", color: "#26215C" },
    { key: "invno", label: "Invoice no", group: "invoice", bg: "#E1F5EE", color: "#04342C" },
    { key: "date", label: "Date", group: "invoice", bg: "#EDF9F4", color: "#04342C" },
    { key: "invval", label: "Invoice ₹", group: "amounts", bg: "#FFF3DC", color: "#412402" },
    { key: "taxval", label: "Taxable ₹", group: "amounts", bg: "#FFF8EC", color: "#412402" },
    { key: "cgst", label: "CGST ₹", group: "amounts", bg: "#FFFAEF", color: "#412402" },
     { key: "igst", label: "IGST ₹", group: "amounts", bg: "#FFFAEF", color: "#412402" }, // NEW
    { key: "sgst", label: "SGST ₹", group: "amounts", bg: "#FFFDF5", color: "#412402" },
    { key: "remarks", label: "Remarks", group: "status", bg: "#E6F1FB", color: "#042C53" },
    { key: "isd", label: "ISD", group: "status", bg: "#EDF4FD", color: "#042C53" },
    { key: "table", label: "Table", group: "status", bg: "#F0F6FD", color: "#042C53" },
  ];

  const groupHeaders = [
    { label: "Supplier", span: 3, bg: "#CECBF6", color: "#26215C" },
    { label: "Invoice", span: 2, bg: "#9FE1CB", color: "#04342C" },
    { label: "Tax amounts", span: 5, bg: "#FAC775", color: "#412402" },
    { label: "Status", span: 3, bg: "#B5D4F4", color: "#042C53" },
  ];

  // map grid column key -> filterColumns key (for placing filter inputs under the right header)
  const filterKeyMap = {
    type: "invoice_type",
    gstin: "org_gstin",
    name: "name",
    invno: "invoice_no",
    date: "invoice_date",
    remarks: "remarks",
    isd: "isd_applicable",
    table: "table_type",
  };

  // ── Manual selection submit: records carry the rows, isUploaded:false ──
  const handleSubmitClick = () => {
    const unselectedModified = [...modifiedIsd].filter((id) => !selected.has(id));
    if (unselectedModified.length > 0) {
      toast.error("Please select the checkbox for rows where you changed the ISD value before submitting.");
      return;
    }

    const records = [...selected].map((id) => {
      const row = localRows.find((r) => r.id === id);
      return { ...row, selected: true, recon_id: reconId };
    });

    onSubmit({ records, recon_id: reconId, is_uploaded: false });
  };

  const handleDownloadTemplate = async (recon_id) => {
    try {
      setDownloadingTemplate(true);
      const response = await axios.get(`/export_gstr6_recon/${recon_id}`);
      const { filename, content_type, data } = response.data;
      downloadBase64Excel(content_type, data, filename);
    } catch (error) {
      toast.error("Export failed", { position: "top-right", autoClose: 3000 });
    }
    finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 2, overflow: "hidden",
          width: "96vw", maxWidth: "96vw",     // FIX: wider popup
          height: "92vh", maxHeight: "92vh",   // FIX: taller popup
        }
      }}>

      {/* Title */}
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        borderBottom: "0.5px solid #D3D1C7", pb: 1.5, pt: 2,
      }}>
        <Box>
          <Typography variant="h6" fontWeight={500}>GSTR-6 Reconciliation Data</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ border: "0.5px solid #D3D1C7", borderRadius: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>

        {/* Meta bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ fontSize: 12, px: 1.5, py: 0.5, borderRadius: 20, bgcolor: "#E1F5EE", color: "#085041", fontWeight: 500 }}>
              {rowCount} total records
            </Box>
            <Box sx={{ fontSize: 12, px: 1.5, py: 0.5, borderRadius: 20, bgcolor: "#EEEDFE", color: "#3C3489", fontWeight: 500 }}>
              {selected.size} selected
            </Box>

            <button
              type="button"
              onClick={handleFetchAllRecords}
              disabled={loadingAllRecords}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 12, padding: "4px 12px", borderRadius: 20,
                border: `1px solid ${allRecordsMode ? "#3C3489" : "#534AB7"}`,
                background: allRecordsMode ? "#534AB7" : "#fff",
                color: allRecordsMode ? "#fff" : "#534AB7",
                fontWeight: 500, cursor: loadingAllRecords ? "not-allowed" : "pointer",
                opacity: loadingAllRecords ? 0.7 : 1,
              }}
            >
              {loadingAllRecords && (
                <CircularProgress size={11} thickness={5} sx={{ color: allRecordsMode ? "#fff" : "#534AB7" }} />
              )}
              All Records
            </button>

            {/* active filter indicator + clear all */}
            {Object.values(columnFilters).some(isFilterActive) && (
              <Box
                onClick={clearColumnFilters}
                sx={{
                  fontSize: 12, px: 1.5, py: 0.5, borderRadius: 20,
                  bgcolor: "#FEF0EE", color: "#A63020", fontWeight: 500,
                  cursor: "pointer", userSelect: "none",
                }}
              >
                ✕ Clear filters
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", marginTop: "3px" }}>
            {/* Download Template */}
            <button
              type="button"
              onClick={() => handleDownloadTemplate(reconId)}
              disabled={downloadingTemplate}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", fontSize: 12.5, fontWeight: 500,
                border: "0.5px solid #B4B2A9", borderRadius: 8,
                background: "#fff", color: "#5F5E5A",
                cursor: downloadingTemplate ? "not-allowed" : "pointer",
                opacity: downloadingTemplate ? 0.6 : 1,
              }}
            >
              {downloadingTemplate ? (
                <>
                  <CircularProgress size={12} thickness={5} sx={{ color: "#5F5E5A" }} />
                  Downloading...
                </>
              ) : (
                <>⬇ Download Template</>
              )}
            </button>

            {/* Upload Template */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingTemplate}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", fontSize: 12.5, fontWeight: 500,
                border: "0.5px solid #B4B2A9", borderRadius: 8,
                background: "#fff", color: "#5F5E5A",
                cursor: uploadingTemplate ? "not-allowed" : "pointer",
                opacity: uploadingTemplate ? 0.6 : 1,
              }}
            >
              {uploadingTemplate ? (
                <>
                  <CircularProgress size={12} thickness={5} sx={{ color: "#5F5E5A" }} />
                  Uploading...
                </>
              ) : (
                <>⬆ Upload Template</>
              )}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              multiple
              style={{ display: "none" }}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  setUploadingTemplate(true);
                  try {
                    await onFilesAdded(files, async () => {
                      const ok = await onSubmit({ records: [], recon_id: reconId, is_uploaded: true });
                      if (ok) {
                        await fetchPage(page);
                        onClose();
                      }
                    });
                  } finally {
                    setUploadingTemplate(false);
                  }
                }
                e.target.value = "";
              }}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  onFilesAdded(files, async () => {
                    const ok = await onSubmit({ records: [], recon_id: reconId, is_uploaded: true });
                    if (ok) {
                      await fetchPage(page);
                      onClose();
                    }
                  });
                }
                e.target.value = "";
              }}
            />

            {/* Search */}
            {/* <Box sx={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search invoice / supplier…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: "6px 10px 6px 32px", fontSize: 13,
                  border: "0.5px solid #B4B2A9", borderRadius: 8,
                  background: "var(--color-background-primary)",
                  color: "var(--color-text-primary)", width: 220,
                }}
              />
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#888780" }}>🔍</span>
            </Box> */}
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "68vh",   // FIX: taller table area to match bigger dialog
          border: "0.5px solid #D3D1C7",
          borderRadius: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 1050 }}>
            <thead>
              {/* Group row */}
              <tr>
                <th style={{ ...thBase, background: "#F1EFE8", width: 40, borderBottom: "none", position: "sticky", top: 0, zIndex: 3 }}></th>
                {groupHeaders.map((g) => (
                  <th key={g.label} colSpan={g.span}
                    style={{ ...thBase, background: g.bg, color: g.color, textAlign: "center", borderBottom: "none", fontSize: 11, position: "sticky", top: 0, zIndex: 3 }}>
                    {g.label}
                  </th>
                ))}
              </tr>

              <tr>
                <th style={{ ...thBase, background: "#F1EFE8", width: 40, position: "sticky", top: 32, zIndex: 3 }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                    aria-label="Select all"
                    style={{ accentColor: "#534AB7", width: 14, height: 14, cursor: "pointer" }}
                  />
                </th>
                {cols.map((c) => (
                  <th key={c.key} style={{
                    ...thBase, background: c.bg, color: c.color,
                    textAlign: ["invval", "taxval", "cgst", "igst", "sgst"].includes(c.key) ? "right" : "left",
                    position: "sticky", top: 32, zIndex: 3
                  }}>
                    {c.label}
                  </th>
                ))}
              </tr>

              {/* Filter row (multi-select for dropdown columns, text for others) */}
              <tr>
                <th style={{ ...thBase, background: "#FBFAF7", width: 40, position: "sticky", top: 64, zIndex: 3 }}></th>
                {cols.map((c) => {
                  const filterKey = filterKeyMap[c.key];
                  if (!filterKey) {
                    return <th key={c.key} style={{ ...thBase, background: "#FBFAF7", position: "sticky", top: 64, zIndex: 3 }}></th>;
                  }
                  const colDef = filterColumns.find((fc) => fc.key === filterKey);
                  return (
                    <th key={c.key} style={{ ...thBase, background: "#FBFAF7", padding: "6px 8px", position: "sticky", top: 64, zIndex: 3 }}>
                      {colDef.type === "select" ? (
                        <MultiSelectFilterDropdown
                          options={filterOptions[filterKey] || []}
                          selected={columnFilters[filterKey] || []}
                          onChange={(vals) => handleColumnFilterChange(filterKey, vals)}
                          align={c.key === "table" ? "right" : "left"} // FIX: keep "Table" filter panel inside view
                        />
                      ) : (
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters[filterKey] || ""}
                          onChange={(e) => handleColumnFilterChange(filterKey, e.target.value)}
                          style={{
                            width: "100%", fontSize: 11, padding: "3px 6px",
                            border: "1px solid #D3D1C7", borderRadius: 6,
                            background: columnFilters[filterKey] ? "#EEEDFE" : "#fff",
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map((row) => {
                  const isSel = selected.has(row.id);
                  const dimStyle = isSel ? { filter: "brightness(0.93)" } : {};
                  return (
                    <tr key={row.id} onClick={() => toggleRow(row.id)}
                      style={{ borderBottom: "0.5px solid #E8E6DF", cursor: "pointer", ...dimStyle }}>
                      <td style={{ padding: "8px 10px", background: "#F1EFE8" }}>
                        <input type="checkbox" checked={isSel} onChange={() => toggleRow(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ accentColor: "#534AB7", width: 14, height: 14, cursor: "pointer" }} />
                      </td>
                      <td style={{ padding: "8px 10px", background: "#EEEDFE" }}>
                        <span style={pillStyle("INVOICE")}>{row.invoice_type}</span>
                      </td>
                      <td style={{ padding: "8px 10px", background: "#F4F2FE", fontSize: 11, color: "#534AB7" }}>
                        {row.org_gstin}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#F9F8FF", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={row.name}>{row.name}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#E1F5EE", fontWeight: 500, color: "#0F6E56" }}>
                        {row.invoice_no}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#EDF9F4", color: "#1D9E75", fontSize: 12 }}>
                        {row.invoice_date}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#FFF3DC", textAlign: "right", color: "#633806", fontWeight: 500 }}>
                        ₹{fmt(row.invoice_value)}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#FFF8EC", textAlign: "right", color: "#854F0B" }}>
                        ₹{fmt(row.tax_value)}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#FFFAEF", textAlign: "right", color: "#BA7517" }}>
                        ₹{fmt(row.central_tax)}
                      </td>
                       <td style={{ padding: "8px 10px", background: "#FFFAEF", textAlign: "right", color: "#BA7517" }}>
                        ₹{fmt(row.integrated_tax)}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#FFFDF5", textAlign: "right", color: "#BA7517" }}>
                        ₹{fmt(row.state_tax)}
                      </td>
                      <td style={{ padding: "8px 10px", background: "#E6F1FB" }}>
                        <span style={pillStyle("match")}>{row.remarks}</span>
                      </td>
                      <td style={{ padding: "8px 10px", background: "#EDF4FD" }}>
                        <select
                          value={row.isd_applicable}
                          onChange={(e) => handleIsdChange(row.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: 11, fontWeight: 500, padding: "2px 6px", borderRadius: 20,
                            border: "1px solid #B4B2A9", cursor: "pointer",
                            background: row.isd_applicable === "Yes" ? "#C0DD97" : "#F5C4B3",
                            color: row.isd_applicable === "Yes" ? "#173404" : "#4A1B0C",
                          }}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </td>
                      <td style={{ padding: "8px 10px", background: "#F0F6FD" }}>
                        <span style={pillStyle("table")}>{row.table_type}</span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={14} style={{ textAlign: "center", padding: 40, color: "#888780" }}>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2, py: 1.5, borderTop: "0.5px solid #D3D1C7", flexWrap: "wrap", gap: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filtered.length} of {total} records
          </Typography>

          {/* Pagination controls */}
          {/* Pagination controls */}
{!allRecordsMode && !Object.values(columnFilters).some(isFilterActive) && (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <button
      type="button"
      disabled={page === 0 || pageLoading}
      onClick={() => fetchPage(page - 1)}
      style={{
        padding: "4px 10px", fontSize: 13, borderRadius: 6,
        border: "1px solid #B4B2A9", background: "#fff",
        cursor: page === 0 || pageLoading ? "not-allowed" : "pointer",
        opacity: page === 0 || pageLoading ? 0.5 : 1,
      }}
    >
      Prev
    </button>
    <Typography variant="caption">
      {pageLoading ? "Loading..." : `Page ${page + 1} of ${totalPages}`}
    </Typography>
    <button
      type="button"
      disabled={page >= totalPages - 1 || pageLoading}
      onClick={() => fetchPage(page + 1)}
      style={{
        padding: "4px 10px", fontSize: 13, borderRadius: 6,
        border: "1px solid #B4B2A9", background: "#fff",
        cursor: page >= totalPages - 1 || pageLoading ? "not-allowed" : "pointer",
        opacity: page >= totalPages - 1 || pageLoading ? 0.5 : 1,
      }}
    >
      Next
    </button>
  </Box>
)}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <button onClick={onClose}
            style={{ background: "none", border: "0.5px solid #B4B2A9", borderRadius: 8, padding: "8px 16px", fontSize: 13.5, cursor: "pointer", color: "#5F5E5A" }}>
            Cancel
          </button>
          <button
            disabled={selected.size === 0}
            onClick={handleSubmitClick}
            style={{
              background: selected.size > 0 ? "#534AB7" : "#B4B2A9",
              color: "#fff", border: "none", borderRadius: 8,
              padding: "8px 22px", fontSize: 13.5, fontWeight: 500,
              cursor: selected.size > 0 ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", gap: 6,
            }}>
            ✓ {selected.size > 0 ? `Submit ${selected.size} record${selected.size > 1 ? "s" : ""}` : "Submit selected"}
          </button>
        </Box>
      </Box>
    </Dialog>
  );
};

/* ─── Gstr6ReconDataGrid ────────────────────────────────────────────── */
const Gstr6ReconDataGrid = ({ rows, setRows, rowCount, loading, page, setPage, reconId, isISDUploaded, setOpenDragDrop, setFilingOutput, sortModel, setSortModel, filterModel, setFilterModel, fetchDataGridData }) => {
  const columns = isISDUploaded ? ISD_COLUMNS : RECON_COLUMNS;
  const dataGridURL = isISDUploaded ? "/get_gstr6_isd_filing_data/" : "/get_gstr6_recon_for_filing/";
  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkISD, setBulkISD] = useState("");

  const debouncedFetch = useMemo(() =>
    debounce((model) => {
      const filter = model.items?.[0];
      if (!("value" in filter)) return;
      const hasValue = filter && "value" in filter && filter.value && filter.value !== "";
      setPage(0);
      fetchDataGridData(reconId, dataGridURL, 0, null, null, hasValue ? filter : null);
    }, 800),
    [dataGridURL, fetchDataGridData, reconId, setPage]
  );

  useEffect(() => { return () => debouncedFetch.cancel(); }, [debouncedFetch]);

  const onFilterModelChange = useCallback((model) => {
    setFilterModel(model);
    debouncedFetch(model);
  }, [debouncedFetch, setFilterModel]);

  const onPageChange = useCallback((p) => {
    setPage(p);
    fetchDataGridData(reconId, dataGridURL, p);
  }, [reconId, dataGridURL, fetchDataGridData, setPage]);

  const onSortModelChange = useCallback((model) => {
    setSortModel(model);
    fetchDataGridData(reconId, dataGridURL, page, model[0]?.field ?? null, model[0]?.sort ?? null);
  }, [reconId, dataGridURL, page, fetchDataGridData, setSortModel]);

  const handleBulkEdit = useCallback(async () => {
    if (!bulkISD || selectionModel.length === 0) return;
    const customerGstin = localStorage.getItem("gst");
    const updatedRows = rows
      .filter((row) => selectionModel.includes(row.id))
      .map((row) => ({
        id: row.id, isd_applicable: bulkISD,
        place_of_supply: bulkISD === "Yes" ? derivePoS(row.org_gstin, customerGstin) : row.place_of_supply,
      }));
    try {
      const { data } = await axios.post("/update_gstr6_filing_rows", { rows: updatedRows });
      if (!data.status) throw new Error(data.error || "Bulk update failed");
      setRows((prev) => prev.map((row) => { const u = updatedRows.find((u) => u.id === row.id); return u ? { ...row, ...u } : row; }));
      toast.success(`${selectionModel.length} rows updated`);
      setSelectionModel([]);
      setBulkISD("");
    } catch (error) { toast.error(error.message); }
  }, [bulkISD, selectionModel, rows, setRows]);

  return (
    <Box sx={{ height: 450, width: "100%" }}>
      <DataGrid
        experimentalFeatures={{ newEditingApi: true }}
        rows={rows} columns={columns} loading={loading} rowCount={rowCount}
        paginationMode="server" page={page} pageSize={100} onPageChange={onPageChange}
        rowsPerPageOptions={[100]} getRowId={(row) => row.id}
        filterMode="server" filterModel={filterModel} onFilterModelChange={onFilterModelChange}
        sortingMode="server" sortingOrder={["asc", "desc", null]} sortModel={sortModel} onSortModelChange={onSortModelChange}
        editMode="row" processRowUpdate={handleBulkEdit} onProcessRowUpdateError={(err) => console.error(err)}
        isCellEditable={(params) => !isISDUploaded && ["isd_applicable"].includes(params.field)}
        checkboxSelection={!isISDUploaded} selectionModel={selectionModel} onSelectionModelChange={(ids) => setSelectionModel(ids)}
        components={{ Toolbar: CustomToolbar }}
        componentsProps={{ toolbar: { selectionModel, isISDUploaded, bulkISD, setBulkISD, handleBulkEdit, setSelectionModel, setOpenDragDrop, setFilingOutput } }}
      />
    </Box>
  );
};

/* ─── CustomToolbar ─────────────────────────────────────────────────── */
const CustomToolbar = ({ selectionModel, isISDUploaded, bulkISD, setBulkISD, handleBulkEdit, setSelectionModel, setOpenDragDrop, setFilingOutput }) => {
  const handleClick = () => {
    if (!isISDUploaded) { setOpenDragDrop(true); return; }
    setFilingOutput(true);
  };
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between", p: 1 }}>
      {selectionModel.length > 0 && !isISDUploaded ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">{selectionModel.length} rows selected</Typography>
          <Select size="small" value={bulkISD} onChange={(e) => setBulkISD(e.target.value)} displayEmpty sx={{ minWidth: 140 }}>
            <MenuItem value="" disabled>Set ISD Applicable</MenuItem>
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
          <Button variant="contained" size="small" onClick={handleBulkEdit} disabled={!bulkISD}>
            Apply to {selectionModel.length} rows
          </Button>
          <Button variant="outlined" size="small" color="error" onClick={() => setSelectionModel([])}>
            Clear Selection
          </Button>
        </Box>
      ) : <Box />}
      <Button variant="contained" onClick={handleClick}>Continue</Button>
    </GridToolbarContainer>
  );
};

/* ─── FilingOutputSection ───────────────────────────────────────────── */
const FilingOutputSection = ({ reconId, reportPeriod, onReset, downloadBase64Excel }) => {
  const [downloading, setDownloading] = useState(false);
  const [filing, setFiling] = useState(false);

  const filingReportDownload = async () => {
    try {
      setDownloading(true);
      const { data } = await axios.get(`/export_gstr6_filing/${reconId}`);
      if (!data.status) { toast.error(data.error || "Unable to export the file"); return; }
      downloadBase64Excel(data.content_type, data.data, data.filename);
    } catch { toast.error("Failed to download filing report"); }
    finally { setDownloading(false); }
  };

  const saveGstr6 = async () => {
    try {
      setFiling(true);
      const { data } = await axios.post(`/file_gstr6`, { recon_id: reconId, gstin: localStorage.getItem("gst"), ret_period: reportPeriod });
      if (!data.status) throw new Error(data.error || "Failed to file GSTR6");
      onReset();
      toast.success("GSTR6 filed successfully");
    } catch (error) { toast.error(error.message || "Failed to file GSTR6"); }
    finally { setFiling(false); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography variant="h6" gutterBottom>GSTR6 Data for Filing has been uploaded</Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>Download the filing output for your reference before filing.</Typography>
        <Button variant="outlined" onClick={filingReportDownload} disabled={downloading} startIcon={downloading && <CircularProgress size={16} />}>
          {downloading ? "Downloading..." : "Filing Report"}
        </Button>
      </Box>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography variant="body1" color="text.secondary" gutterBottom>Click below to submit your GSTR6 filing.</Typography>
        <Button variant="contained" color="primary" onClick={saveGstr6} disabled={filing} startIcon={filing && <CircularProgress size={16} color="inherit" />}>
          {filing ? "Filing..." : "File GSTR6"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── ISDFileUpload ─────────────────────────────────────────────────── */
const ISDFileUpload = ({ reconId, setIsISDUploaded, setOpenDragDrop, setLoading, uploadFile }) => {
  const onDrop = useCallback(async (acceptedFiles) => {
    try {
      setLoading(true);
      const result = await uploadFile(acceptedFiles[0], "/gstr6_isd_filing_output_upload", { recon_id: reconId, business_entity_id: localStorage.getItem("business_entity_id"), });
      if (!result?.status) throw new Error(result.message);
      setIsISDUploaded(true);
      setOpenDragDrop(false);
    } catch (error) { toast.error(error.message || "Upload Failed, Please try again"); }
    finally { setLoading(false); }
  }, [reconId, setIsISDUploaded, setOpenDragDrop, setLoading, uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
    multiple: false,
  });

  return (
    <div {...getRootProps()} style={{ border: "2px dashed #1976d2", padding: "40px", textAlign: "center", cursor: "pointer", background: isDragActive ? "#f0f8ff" : "white" }}>
      <input {...getInputProps()} />
      {isDragActive ? <Typography variant="h6">Drop the file here...</Typography> : (
        <>
          <Typography variant="h6" gutterBottom>Please Upload The ISD Data (Tables 5, 8, 9)</Typography>
          <Typography variant="body2" color="text.secondary">Template available on the platform. Click "ISD Template" to download.</Typography>
          <Typography variant="body2" mt={1}>Drag & drop Excel file here, or click to select</Typography>
        </>
      )}
    </div>
  );
};

/* ─── GSTR6FilingReport ─────────────────────────────────────────────── */
const GSTR6FilingReport = ({ reconId, loading, filingOutput, openDragDrop, isISDUploaded, setIsISDUploaded, setOpenDragDrop, setFilingOutput, rows, setRows, rowCount, page, setPage, sortModel, setSortModel, filterModel, setFilterModel, fetchDataGridData, reportPeriod, onReset, downloadExcel, handleFileUpload, uploadFile }) => {
  if (!reconId) return null;
  if (loading) {
    return (
      <BoxWrapper boxTitle="GSTR-6 Filing Report">
        <div style={{ padding: "40px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "12px" }}>
          <div className="bouncing-loader"><div /><div /><div /></div>
          <span style={{ fontSize: "14px", color: "#666" }}>Loading...</span>
          <style>{`.bouncing-loader{display:flex;justify-content:center;align-items:center;gap:8px}.bouncing-loader div{width:12px;height:12px;border-radius:50%;background:#1976d2;animation:bounce 0.6s infinite alternate}.bouncing-loader div:nth-child(2){animation-delay:0.2s}.bouncing-loader div:nth-child(3){animation-delay:0.4s}@keyframes bounce{from{transform:translateY(0px);opacity:0.6}to{transform:translateY(-10px);opacity:1}}`}</style>
        </div>
      </BoxWrapper>
    );
  }
  return (
    <BoxWrapper boxTitle="GSTR-6 Filing Report" enableAddButton={!filingOutput && !openDragDrop} addButtonLabel={isISDUploaded ? "Reupload" : "Download Report"} addButtonHandlerFn={downloadExcel} handleFileUpload={handleFileUpload} uploadFileBtnLabel="Upload New Report" uploadFileBtn={!filingOutput && !openDragDrop && !isISDUploaded}>
      {filingOutput ? (
        <FilingOutputSection reconId={reconId} reportPeriod={reportPeriod} onReset={onReset} downloadBase64Excel={downloadBase64Excel} />
      ) : openDragDrop ? (
        <ISDFileUpload reconId={reconId} setIsISDUploaded={setIsISDUploaded} setOpenDragDrop={setOpenDragDrop} setLoading={() => { }} uploadFile={uploadFile} />
      ) : (
        <Gstr6ReconDataGrid rows={rows} setRows={setRows} rowCount={rowCount} loading={loading} page={page} setPage={setPage} reconId={reconId} isISDUploaded={isISDUploaded} setOpenDragDrop={setOpenDragDrop} setFilingOutput={setFilingOutput} sortModel={sortModel} setSortModel={setSortModel} filterModel={filterModel} setFilterModel={setFilterModel} fetchDataGridData={fetchDataGridData} />
      )}
    </BoxWrapper>
  );
};


const GenericFileUpload = ({ onFilesAdded }) => {
  const onDrop = useCallback((acceptedFiles) => {
    onFilesAdded(acceptedFiles);
  }, [onFilesAdded]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
    multiple: true,
    noClick: true,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        borderRadius: 12,
        padding: "28px 24px",
        textAlign: "center",
        background: isDragActive
          ? "linear-gradient(160deg, #EEF4FF 0%, #F5F0FF 100%)"
          : "linear-gradient(160deg, #F4F8FF 0%, #FAF8FF 100%)",
        border: `1.5px dashed ${isDragActive ? "#4D8EF7" : "#C5D8FB"}`,
        transition: "all 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        aspectRatio: "1 / 1",
        maxWidth: 200,
        margin: "0 auto",
        cursor: "default",
      }}
    >
      <input {...getInputProps()} />

      {/* Illustration */}
      <svg width="70" height="58" viewBox="0 0 62 52" fill="none" style={{ marginBottom: 12 }}>
        <defs>
          <linearGradient id="cloudGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#A8C8F8" />
            <stop offset="100%" stopColor="#C4B0F5" />
          </linearGradient>
          <linearGradient id="folderPink" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F9A8C0" />
            <stop offset="100%" stopColor="#F7C5D5" />
          </linearGradient>
          <linearGradient id="folderPurple" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#B8A6E8" />
            <stop offset="100%" stopColor="#D0C4F5" />
          </linearGradient>
        </defs>

        {/* Pink folder */}
        <rect x="0" y="22" width="22" height="16" rx="3" fill="url(#folderPink)" opacity="0.7" />
        <rect x="0" y="19" width="10" height="5" rx="2" fill="url(#folderPink)" opacity="0.7" />

        {/* Purple folder */}
        <rect x="40" y="20" width="22" height="16" rx="3" fill="url(#folderPurple)" opacity="0.7" />
        <rect x="40" y="17" width="10" height="5" rx="2" fill="url(#folderPurple)" opacity="0.7" />

        {/* White photo card back */}
        <rect x="18" y="14" width="20" height="16" rx="3" fill="white" stroke="#D8E8FC" strokeWidth="1.2" opacity="0.85" />
        <circle cx="22" cy="19" r="2" fill="#C5D8FB" />
        <path d="M18 26 L22 22 L26 26 L29 23 L38 30" stroke="#C5D8FB" strokeWidth="1.2" fill="none" strokeLinejoin="round" />

        {/* White photo card front */}
        <rect x="26" y="20" width="20" height="16" rx="3" fill="white" stroke="#D8E8FC" strokeWidth="1.2" />
        <circle cx="30" cy="25" r="2" fill="#C5D8FB" />
        <path d="M26 32 L30 28 L34 32 L37 29 L46 36" stroke="#C5D8FB" strokeWidth="1.2" fill="none" strokeLinejoin="round" />

        {/* Cloud */}
        <ellipse cx="31" cy="10" rx="10" ry="7" fill="url(#cloudGrad)" opacity="0.85" />
        <ellipse cx="24" cy="13" rx="6" ry="5" fill="url(#cloudGrad)" opacity="0.85" />
        <ellipse cx="38" cy="13" rx="6" ry="5" fill="url(#cloudGrad)" opacity="0.85" />
        <rect x="27" y="12" width="8" height="10" rx="1" fill="url(#cloudGrad)" opacity="0.85" />

        {/* Upload arrow */}
        <path d="M31 7 V14 M28 10 L31 7 L34 10" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {/* Text */}
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#2B2A27", lineHeight: 1.4 }}>
        {isDragActive ? "Release to upload" : "Drop your files here"}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "#A0AABF", mt: 0.5 }}>
        or{" "}
        <span
          onClick={open}
          style={{ color: "#4D8EF7", fontWeight: 600, cursor: "pointer" }}
        >
          Browse
        </span>
        <br />
        <span style={{ fontSize: 10.5, color: "#C0C8D8" }}>.xlsx only</span>
      </Typography>
    </div>
  );
};

/* ─── Main GSTR6Filing ──────────────────────────────────────────────── */
export default function GSTR6Filing() {
  const [reportPeriod, setReportPeriod] = useState(null);
  const [forDisplayingFromMonthAndYear, setForDisplayingFromMonthAndYear] = useState(null);
  const [openEstablisConnectionDrawer, setOpenEstablisConnectionDrawer] = useState(false);
  const form = useRef("form");
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [username, setUserName] = useState("");
  const [otpApiData, setOtpApidata] = useState({});
  const [establishConnectionButton, setEstablisConnectionButton] = useState("button-outlined-secondary");
  const [authAPiResData, setAuthApiResData] = useState({});
  const [auth_otp, setAuthOTP] = useState("");
  const [isdDialog, setISDDialogStatus] = useState(false);
  const [isdBranchDetails, setIsdBranchDetails] = useState([]);
  const [newIsdItems, setNewIsdItems] = useState([]);
  const [isdReadOnly, setIsdReadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reconId, setReconId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const pollingRef = useRef(null);
  const [openDragDrop, setOpenDragDrop] = useState(false);
  const [isISDUploaded, setIsISDUploaded] = useState(false);
  const [filingOutput, setFilingOutput] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [tableData, setTableData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [reconPopup, setReconPopup] = useState(false);
  const [reconPopupData, setReconPopupData] = useState([]);
  const [reconPopupReconId, setReconPopupReconId] = useState(null); // added
  const eventSourceRef = useRef(null);
  const sseOpenedRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [uploadLogs, setUploadLogs] = useState([]);


  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);


  const [processingStep, setProcessingStep] = useState(0); // 0: connecting, 1: running, 2: fetching results


  const [gstr6Dialog, setGstr6Dialog] = useState(false);
  const [downloadingIsdTemplate, setDownloadingIsdTemplate] = useState(false);


  const [gstr6Filing, setGstr6Filing] = useState(false);
  const [isdUploadSuccess, setIsdUploadSuccess] = useState(false);


  const [downloadingUploadErrorId, setDownloadingUploadErrorId] = useState(null);
  const [downloadingReportId, setDownloadingReportId] = useState(null);

  const [downloadingErrorId, setDownloadingErrorId] = useState(null);




  const style = {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 400, bgcolor: "background.paper", border: "2px solid #000", boxShadow: 24, p: 4, zIndex: 1300,
  };

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
    };
  }, []);

  const initiateReconSSE = (sseToken) => {
    return new Promise((resolve, reject) => {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      sseOpenedRef.current = false;
      console.log("SSE Starting point");
      const url = `${SSE_BASE_URL}/api/sse/${sseToken}`;
      eventSourceRef.current = new EventSource(url);
      console.log("SSE object created:", url, "readyState:", eventSourceRef.current.readyState);

      eventSourceRef.current.onopen = () => {
        sseOpenedRef.current = true;
        console.log("SSE onopen fired, readyState:", eventSourceRef.current.readyState);
        resolve();
      };

      eventSourceRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.status === "done" || data.status === "Pass") {
            setProcessingStep(2);
            setProcessingMessage("Fetching reconciliation results...");
            if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
            axios.get(`/get_gstr6_recon_for_filing/${data.recon_id}`, { params: { page: 1 } })
              .then((res) => {
                if (res.data.status) {
                  setReconPopupData(res.data.data);
                  setRows(res.data.data);
                  setRowCount(res.data.total);
                  setReconPopup(true);
                  setReconPopupReconId(data.recon_id);
                }
              })
              .catch((err) => {
                console.error("Failed to fetch recon data:", err);
                toast.error("Failed to fetch reconciliation results");
              })
              .finally(() => {
                setIsProcessing(false);
                setProcessingMessage("");
              });
          } else if (data.status === "fail" || data.status === "error") {
            if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
            setIsProcessing(false);
            setProcessingMessage("");
            toast.error(data.message || "Reconciliation failed");
          }
        } catch (err) { console.log("Ignoring non-json SSE message:", event.data); }
      };
      eventSourceRef.current.onerror = (e) => {
        const rs = eventSourceRef.current?.readyState;
        if (rs === 2) {
          if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
          if (!sseOpenedRef.current) {
            reject(new Error("SSE connection failed permanently"));
          } else {
            // connection dropped AFTER it was already streaming — don't leave the user stuck on a spinner
            setIsProcessing(false);
            setProcessingMessage("");
            toast.error("Lost connection while waiting for the result. Please check the status below.");
          }
        }
      };
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const inputHandlerForYearAndFromMonth = (v) => {
    setForDisplayingFromMonthAndYear(v);
    const current_date = new Date(v);
    const yy = current_date.getFullYear().toString();
    const mm = (current_date.getMonth() + 1).toString().padStart(2, "0");
    setReportPeriod(`${mm}${yy}`);
  };

  const handleOpenAndCloseModel = () => {
    setReportPeriod(null);
    setForDisplayingFromMonthAndYear(null);
  };

  const closeEstablisConnectionDrawerHamdler = () => setOpenEstablisConnectionDrawer(false);
  const closeOptModalHandler = () => { setAuthOTP(""); setOpenOTPModal(false); };

  const vyanaAuthHandler = async () => {
    let payload = {
      user_name: username, business_entity_id: localStorage.getItem("business_entity_id"),
      app_key: otpApiData.app_key, transaction_id: otpApiData.transaction_id, otp: auth_otp,
    };
    try {
      const res = await axios.post("/vyana/authenticate", payload);
      if (!res.data.status) throw new Error(res.data.error);
      setOpenOTPModal(false);
      setOpenEstablisConnectionDrawer(false);
      setEstablisConnectionButton("button-connection-established");
      setAuthApiResData({ ...authAPiResData, sek: res.data.sek, auth_token: res.data.auth_token });
      setSnackbar({ open: true, message: res.data.message, severity: "success" });
      setTimeout(() => setEstablisConnectionButton("button-outlined-secondary"), 300000);
    } catch (error) {
      setOpenOTPModal(true);
      setEstablisConnectionButton("button-outlined-secondary");
      setSnackbar({ open: true, message: error.response?.message || error.message, severity: "error" });
    } finally { setUserName(""); }
  };

  const getOtpForAuthHandler = async () => {
    try {
      const payload = { user_name: username, business_entity_id: localStorage.getItem("business_entity_id") };
      const res = await axios.post("/vyana/send_otp", payload);
      if (!res.data.status) {
        setSnackbar({ open: true, message: res.data.error, severity: "error" });
        setOpenOTPModal(false);
        setEstablisConnectionButton("button-outlined-secondary");
        return;
      }
      if (res.data.is_authenticated && !res.data.is_otp_sent) {
        setOpenOTPModal(false);
        if (res.data.message === "Connection established.") { setSuccessMessage(res.data.message); setAuthToken(res.data.auth_token); }
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
        closeEstablisConnectionDrawerHamdler();
      } else if (!res.data.is_authenticated && res.data.is_otp_sent) {
        closeEstablisConnectionDrawerHamdler();
        setOpenOTPModal(true);
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
      } else {
        setOpenOTPModal(true);
        setOtpApidata({ ...otpApiData, transaction_id: res.data.transaction_id, app_key: res.data.app_key });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.message || error.message, severity: "error" });
    }
  };

  const onSubmitHandler = async () => {
    setIsISDUploaded(false);
    setReconId(null);
    setFilingOutput(false);
    setIsProcessing(true);
    setProcessingStep(0);
    setProcessingMessage("Connecting to server...");
    try {
      const sseToken = crypto.randomUUID();
      const requestPayload = {
        org_gstin: localStorage.getItem("gst"),
        report_period: reportPeriod,
        from_month: "none",
        to_month: "none",
        period_type: "custom",
        report_type: "gstr6_filing",
        business_entity_id: localStorage.getItem("business_entity_id"),
        sse_token: sseToken,
        functionality: "GSTR6 Filing",
      };

      await initiateReconSSE(sseToken);
      setProcessingStep(1);
      setProcessingMessage("Reconciliation in progress. This may take a few moments...");

      const res = await axios.post(`/unified_auto_reconciliation`, requestPayload);

      if (!res.data || res.data.status === false) {
        if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
        setIsProcessing(false);
        setProcessingMessage("");
        toast.error("Failed to initiate reconciliation");
        return;
      }

      setReconId(res.data.recon_id);
      setForDisplayingFromMonthAndYear(null);
      // 👇 isProcessing stays TRUE here — the SSE onmessage handler below turns it off
    } catch (error) {
      console.error("onSubmitHandler error:", error);
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      setIsProcessing(false);
      setProcessingMessage("");
      toast.error("Error initiating reconciliation");
    }
  };
  // const onSubmitHandler = async () => {
  //   setIsISDUploaded(false);
  //   setReconId(null);
  //   setFilingOutput(false);
  //   try {
  //     const sseToken = crypto.randomUUID();
  //     const requestPayload = {
  //       org_gstin: localStorage.getItem("gst"),
  //       report_period: reportPeriod,
  //       from_month: "none",
  //       to_month: "none",
  //       period_type: "custom",
  //       report_type: "gstr6_filing",
  //       business_entity_id: localStorage.getItem("business_entity_id"),
  //       sse_token: sseToken,
  //       functionality: "GSTR6 Filing",
  //     };

  //     await initiateReconSSE(sseToken);
  //     console.log("SSE confirmed open. Starting recon POST.");
  //     setProcessingMessage("Reconciliation started. Waiting for result...");

  //     const res = await axios.post(`/unified_auto_reconciliation`, requestPayload);
  //     console.log("Recon API response:", res.data);

  //     if (!res.data || res.data.status === false) {
  //       if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
  //       setIsProcessing(false);
  //       setProcessingMessage("");
  //       toast.error("Failed to initiate reconciliation");
  //       return;
  //     }

  //     toast.info("Reconciliation initiated. Waiting for result...");

  //     // const response = await axios.post(`/gstr6_report_generation`, {
  //     //   business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
  //     //   report_period: reportPeriod,
  //     //   org_gstin: localStorage.getItem("gst"),
  //     // });
  //     setReconId(res.data.recon_id);
  //     setForDisplayingFromMonthAndYear(null);
  //   } catch (error) {
  //     console.error("onSubmitHandler error:", error);
  //     if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
  //     setIsProcessing(false);
  //     setProcessingMessage("");
  //     toast.error("Error initiating reconciliation");
  //   }
  // };

  const getIsdBranchDetails = async () => {
    try {
      const response = await axios(`/get_isd_branch_details/${localStorage.getItem("pan")}`);
      if (response.data.status) setIsdBranchDetails(response.data.isd_branch_details);
    } catch (error) { console.error(error); }
  };

  const getISDtemplate = async () => {
    try {
      const { data } = await axios("get_isd_template");
      if (!data.status) throw new Error("Something went wrong, Please try again");
      const link = document.createElement("a");
      link.href = data.url;
      link.download = "gstr6_isd_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error(error); toast.error(error); }
  };


  const handleFilesAdded = async (files, onComplete) => {
    const newFiles = files.map((f) => ({ file: f, name: f.name, size: f.size, status: "Pending" }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setUploading(true);

    let hasError = false; // ← track failures across the batch

    for (const f of newFiles) {
      try {
        const result = await uploadFile(f.file, "/gstr6_upload_file", {
          recon_id: reconId,
          gstin: localStorage.getItem("gst"),
          bussiness_entity_id: localStorage.getItem("business_entity_id"),
        });

        const isFailed = !result?.status;
        const fileId = result?.file_id;

        setUploadedFiles((prev) =>
          prev.map((item) =>
            item.file === f.file
              ? { ...item, status: isFailed ? "Failed" : "Uploaded", file_id: fileId }
              : item
          )
        );

        if (isFailed) {
          hasError = true;
          setSnackbar({ open: true, message: result?.error || "Upload failed", severity: "error" });
        } else {
          // ← added: show the queued message on success
          setSnackbar({ open: true, message: result?.message || "Upload queued for processing", severity: "success" });
        }
      } catch (err) {
        hasError = true;
        setSnackbar({ open: true, message: err.message || "Upload failed", severity: "error" });
        setUploadedFiles((prev) =>
          prev.map((item) => (item.file === f.file ? { ...item, status: "Failed" } : item))
        );
      }
    }

    setUploading(false);

    if (hasError) {
      // Don't refresh the recon table or submit selected records if the upload itself failed
      return;
    }

    if (onComplete) {
      // Backend processes the upload asynchronously (status: "queued"),
      // so wait before calling selected_records + closing the dialog.
      // setTimeout(() => {
      onComplete(); // runs onSubmit({..., is_uploaded:true}) -> fetchPage(page) -> onClose() (only if selected_records succeeds)
      // }, 10000); // 20 seconds
    } else {
      getUploadLogs();
    }
  };


  const handleIsdFilesAdded = async (files) => {
    const newFiles = files.map((f) => ({ file: f, name: f.name, size: f.size, status: "Pending" }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setUploading(true);

    let anySucceeded = false; // ← added

    for (const f of newFiles) {
      try {
        const result = await uploadFile(f.file, "/gstr6_isd_filing_output_upload", {
          recon_id: reconId,
          business_entity_id: localStorage.getItem("business_entity_id"),
        });

        const isFailed = !result?.status;
        const fileId = result?.file_id;

        setUploadedFiles((prev) =>
          prev.map((item) =>
            item.file === f.file
              ? { ...item, status: isFailed ? "Failed" : "Uploaded", file_id: fileId }
              : item
          )
        );

        if (isFailed) {
          setSnackbar({ open: true, message: result?.error || "Upload failed", severity: "error" });
        } else {
          anySucceeded = true; // ← added
        }
      } catch (err) {
        setSnackbar({ open: true, message: err.message || "Upload failed", severity: "error" });
        setUploadedFiles((prev) =>
          prev.map((item) => (item.file === f.file ? { ...item, status: "Failed" } : item))
        );
      }
    }

    setUploading(false);
    if (anySucceeded) setIsdUploadSuccess(true); // ← enables the GSTR 6 File button
    getUploadLogs();
  };

  // const handleFilesAdded = async (files) => {
  //   const newFiles = files.map((f) => ({ file: f, name: f.name, size: f.size, status: "Pending" }));
  //   setUploadedFiles((prev) => [...prev, ...newFiles]);
  //   setUploading(true);

  //   for (const f of newFiles) {
  //     try {
  //       const result = await uploadFile(f.file, "/gstr6_upload_file", {
  //         recon_id: reconId,
  //         gstin: localStorage.getItem("gst"),
  //       });

  //       if (!result || !result.status) {
  //         // added: surface backend error in Snackbar
  //         setSnackbar({ open: true, message: result?.error || "Upload failed", severity: "error" });
  //       }

  //       setUploadedFiles((prev) =>
  //         prev.map((item) =>
  //           item.file === f.file ? { ...item, status: result?.status ? "Uploaded" : "Failed" } : item
  //         )
  //       );
  //     } catch (err) {
  //       // added: surface unexpected/network error too
  //       setSnackbar({ open: true, message: err.message || "Upload failed", severity: "error" });
  //       setUploadedFiles((prev) =>
  //         prev.map((item) => (item.file === f.file ? { ...item, status: "Failed" } : item))
  //       );
  //     }
  //   }

  //   setUploading(false);
  //   getUploadLogs();
  // };

  // const downloadErrorReport = async (file_id) => {
  //   try {
  //     const response = await axios.get(`/gstr6a_excel_report_blob/${file_id}`);
  //     const { filename, content_type, file } = response.data; // ✅ use "file" not "data"
  //     downloadBase64Excel(content_type, file, filename);
  //   } catch (error) {
  //     toast.error("Export failed", { position: "top-right", autoClose: 3000 });
  //   }
  // }


  const downloadUploadReport = async (recon_id) => {
    try {
      const response = await axios.get(`/export_gstr6_recon/${recon_id}`);
      const { filename, content_type, data } = response.data;
      downloadBase64Excel(content_type, data, filename);
    } catch (error) { toast.error("Export failed", { position: "top-right", autoClose: 3000 }); }

  }

  useEffect(() => {
    setNewIsdItems(isdBranchDetails.map((item) => ({ ...item, is_modified: false })));
  }, [isdBranchDetails]);

  useEffect(() => { getIsdBranchDetails(); }, []);

  const handleClose = (revert = true) => {
    setISDDialogStatus(false);
    setIsdReadOnly(true);
    if (revert) setNewIsdItems(isdBranchDetails);
  };

  const handleAddRow = () => {
    setNewIsdItems((prev) => [...prev, { branch_name: "", gst: "", pan: localStorage.getItem("pan"), turnover: "", type: "new", is_modified: true }]);
  };

  const updateISDItems = async () => {
    try {
      const updatedItems = newIsdItems.filter((item) => (item.is_modified || item.type === "new") && item.branch_name && item.gst);
      if (updatedItems.length === 0) { toast.error("No changes to save"); return; }
      const response = await axios.post(`/update_isd_details`, { data: updatedItems });
      if (!response.data.status) throw new Error(response.data.message || "Couldn't update the ISD details");
      toast.success(response.data.message);
      await getIsdBranchDetails();
      handleClose(false);
    } catch (error) { toast.error(error.message); }
  };

  const handleIsdFormFieldChange = (field, index) => (e) => {
    let value = e.target.value;
    if (field === "turnover") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) { toast.error("Turnover should be a valid number"); return; }
      value = value === "" ? null : Number(value);
    }
    setNewIsdItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value, is_modified: true } : item));
  };

  const fetchDataGridData = useCallback(async (recon_id, url, page = 0, sort_by = null, sort_order = null, filters = null) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${url}${recon_id}`, { params: { page: page + 1, sort_by, sort_order, filters } });
      if (!data.status) throw new Error(data.error || "Request failed");
      setRows(data.data);
      setRowCount(data.total);
    } catch (error) {
      toast.error(error.message, { position: "top-right", autoClose: 3000 });
    } finally { setLoading(false); }
  }, []);

  const pollReconStatus = useCallback(async (recon_id) => {
    let retryCount = 0;
    const MAX_RETRIES = 30;
    const dataGridURL = isISDUploaded ? "/get_gstr6_isd_filing_data/" : "/get_gstr6_recon_for_filing/";
    const poll = async () => {
      try {
        const { data } = await axios.get(`${dataGridURL}${recon_id}`, { params: { page: 1 } });
        if (!data.status) throw new Error(data.error || "Request failed");
        const status = data.recon_status?.toLowerCase();
        if (["failed", "error", "fail"].includes(status)) {
          clearTimeout(pollingRef.current); setLoading(false);
          toast.error("Reconciliation failed. Please try again.", { position: "top-right", autoClose: 3000 });
          return;
        }
        if (["pass", "completed", "passed", "complete"].includes(status)) {
          clearTimeout(pollingRef.current); setRows(data.data); setRowCount(data.total); setLoading(false); return;
        }
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
          setLoading(false);
          toast.error("Recon is taking too long. Please refresh and try again.", { position: "top-right", autoClose: 3000 });
          return;
        }
        pollingRef.current = setTimeout(poll, 3000);
      } catch (error) {
        setLoading(false);
        toast.error(error.message, { position: "top-right", autoClose: 3000 });
      }
    };
    poll();
  }, [isISDUploaded]);


  const uploadFile = useCallback(async (file, url, extraParams = {}) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(extraParams).forEach(([key, value]) => formData.append(key, value));
      const { data } = await axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
      return data; // ✅ returns {status: false, error: "Missing columns..."} as-is
    } catch (error) {
      // ✅ if axios throws (4xx/5xx), still return the response body
      return error.response?.data ?? { status: false, error: error.message };
    }
  }, []);

  // const uploadFile = useCallback(async (file, url, extraParams = {}) => {
  //   try {
  //     const formData = new FormData();
  //     formData.append("file", file);
  //     Object.entries(extraParams).forEach(([key, value]) => formData.append(key, value));
  //     const { data } = await axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
  //     return data;
  //   } catch (error) { console.error(error.response?.data ?? "Something went wrong."); return null; }
  // }, []);

  // const handleFileUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   const validTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
  //   if (!validTypes.includes(file.type)) { toast.error("Please upload a valid Excel file (.xlsx)"); return; }
  //   try {
  //     setLoading(true);
  //     const data = await uploadFile(file, "/gstr6_upload_file", { recon_id: reconId, gstin: localStorage.getItem("gst") });
  //     if (!data) throw new Error(data?.error || "Upload failed");
  //     if (!data.status) {
  //       if (data.file) downloadBase64Excel(data.content_type, data.file, data.filename);
  //       throw new Error(data.error || "Upload failed");
  //     }
  //   } catch (error) { toast.error(error.message); }
  //   finally { setLoading(false); e.target.value = ""; }
  // };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
    if (!validTypes.includes(file.type)) { toast.error("Please upload a valid Excel file (.xlsx)"); return; }
    try {
      setLoading(true);
      const data = await uploadFile(file, "/gstr6_upload_file", { recon_id: reconId, gstin: localStorage.getItem("gst") });
      if (!data) throw new Error(data?.error || "Upload failed");
      if (!data.status) {
        if (data.file) downloadBase64Excel(data.content_type, data.file, data.filename);
        throw new Error(data.error || "Upload failed");
      }
      // ← nothing happens here on success — that's the bug
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); e.target.value = ""; }
  };

  const downloadExcel = async (recon_id) => {
    try {
      const response = await axios.get(`/export_gstr6_filing/${recon_id}`);
      const { status, error: apiError, filename, content_type, data } = response.data;

      // Case 1: API responds 200 OK but with status:false in the body
      if (status === false) {
        setSnackbar({ open: true, message: apiError || "Export failed", severity: "error" });
        return;
      }

      downloadBase64Excel(content_type, data, filename);
    } catch (error) {
      // Case 2: API responds with a non-2xx status code
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Export failed";
      setSnackbar({ open: true, message, severity: "error" });
    }
  };

  const downloadExcelISDTemplate = async (recon_id) => {
    try {
      setDownloadingIsdTemplate(true);
      const response = await axios.get(`/export_gstr6_isd_template/${recon_id}`);
      const { status, error: apiError, filename, content_type, data } = response.data;

      if (status === false) {
        setSnackbar({ open: true, message: apiError || "Export failed", severity: "error" });
        return;
      }

      downloadBase64Excel(content_type, data, filename);
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Export failed";
      setSnackbar({ open: true, message, severity: "error" });
    }
    finally {
      setDownloadingIsdTemplate(false);
    }
  };
  const downloadErrorExcel = async (log_id) => {
    try {
      const response = await axios.get(`/gstr6a_error_report/${log_id}`);
      const { status, error: apiError, description, file, extension } = response.data;

      if (status !== "pass") {
        setSnackbar({
          open: true,
          message: apiError || description || "Export failed",
          severity: "error",
        });
        return;
      }

      const content_type = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const filename = `gstr6a_failed_report_${log_id}${extension}`;

      downloadBase64Excel(content_type, file, filename);
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.description ||
        "Export failed";
      setSnackbar({ open: true, message, severity: "error" });
    }
  };

  const handleGstr6FilingSubmit = async () => {
    try {
      setGstr6Filing(true);
      const { data } = await axios.post("/gstr6_filing_save_passthrough", {
        recon_id: reconId,
        gstin: localStorage.getItem("gst"),
      });
      if (!data.status) throw new Error(data.error || "Failed to file GSTR6");
      setSnackbar({ open: true, message: data.message || "GSTR6 filed successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Failed to file GSTR6", severity: "error" });
    } finally {
      setGstr6Filing(false);
    }
  };

  const handleClick = (recon_id) => {
    setReconId(recon_id);
    setUploadDialog(true);
    setIsdUploadSuccess(false);
    getUploadLogs();
  }

  const handleReset = useCallback(() => {
    setReconId(null); setRows([]); setRowCount(0); setIsISDUploaded(false);
  }, []);

  const getLogs = () => {
    axios.get(`/gstr6a_log/${localStorage.getItem("business_entity_id")}`)
      .then((res) => { setTableData(res.data.records || []); })
      .catch((err) => console.log(err));
  };

  useEffect(() => { getLogs(); }, []);

  const handleReconPopupSubmit = async (payload) => {
    try {
      const { records, recon_id, is_uploaded } = payload;

      const body = {
        business_entity_id: localStorage.getItem("business_entity_id"),
        recon_id,
        is_uploaded,
        records,
      };

      const { data } = await axios.post("/gstr6a/selected_records", body);
      if (!data.status) throw new Error(data.error || "Submit failed");

      setSnackbar({ open: true, message: data.message, severity: "success" });

      if (!is_uploaded) {
        setReconPopup(false);
      }

      return true; // ← tells the caller the API call succeeded
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Submit failed", severity: "error" });
      return false; // ← tells the caller it failed, so the dialog stays open
    }
  };

  const handleCloseUpload = () => {
    setUploadDialog(false);
  }


  const getUploadLogs = () => {
    if (!reconId) return; // no recon selected yet, nothing to fetch
    axios.get(`/gstr6_isd_upload_log/${localStorage.getItem("business_entity_id")}/${reconId}`)
      .then((res) => { setUploadLogs(res.data.records || []); })
      .catch((err) => console.log(err));
  };

  useEffect(() => { getUploadLogs(); }, [reconId]);


  // const getUploadLogs = () => {
  //   axios.get(`/gstr6_isd_upload_log/${localStorage.getItem("business_entity_id")}`)
  //     .then((res) => { setUploadLogs(res.data.records || []); })
  //     .catch((err) => console.log(err));
  // };

  useEffect(() => { getUploadLogs(); }, []);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", padding: "15px 20px", backgroundColor: "#f5f5f5", height: "auto", position: "relative" }}>
        <IconButton
          onClick={() => window.history.back()}
          size="small"
          sx={{
            position: "absolute",
            left: 20,
            top: "50%",
            transform: "translateY(-50%)",
            border: "0.5px solid #D3D1C7",
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        {successMessage === "Connection established." ? (
          <button variant="contained" className="button-outlined-primary" color="success"
            style={{ fontSize: "13px", padding: "4px 8px", bottom: "4px", backgroundColor: "#03406d", color: "white" }}>
            Connection Established
          </button>
        ) : (
          <button className={establishConnectionButton} onClick={() => setOpenEstablisConnectionDrawer(true)}>
            {establishConnectionButton === "button-outlined-secondary" ? "Establish Connection" : "Connection Established"}
          </button>
        )}
      </Box>

      <BoxWrapper boxTitle="GSTR-6 Filing"
      // enableAddButton={true}
      //  addButtonLabel={"ISD Template"}
      //   addButtonHandlerFn={getISDtemplate} 
      //   enableSecondButton={true}
      //    secondButtonLabel={"ISD Branch"} 
      //    secondButtonHandlerFn={() => setISDDialogStatus(true)}
      >
        <ValidatorForm onSubmit={onSubmitHandler} onError={(errors) => console.log(errors)}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={5}>
              <label style={{ display: "block", marginBottom: "4px" }}>Reporting Month<span style={{ color: "red" }}>*</span>:</label>
              <ValidatedDatePicker placeholder="Month & Year" errorMessages={["This field is required"]} validators={["required"]} value={forDisplayingFromMonthAndYear} onChange={(v) => inputHandlerForYearAndFromMonth(v)} fullWidth={true} />
            </Grid>
            <Grid item xs={4} sm={1}>
              <button className="button-primary" type="submit" style={{ width: "100%", padding: "8px", fontSize: "14px", marginTop: "1.5rem" }}>Submit</button>
            </Grid>
            <Grid item xs={4} sm={1}>
              <button className="button-outlined-secondary" type="button" onClick={handleOpenAndCloseModel} style={{ width: "100%", padding: "8px", fontSize: "14px", marginTop: "1.5rem" }}>Cancel</button>
            </Grid>
          </Grid>
        </ValidatorForm>
      </BoxWrapper>

      <BoxWrapper boxTitle="GSTR-6 Filing Report" enableSearchInput={true} searchInputPlaceholder="Search..." enableAddButton={true} addButtonLabel="Refresh" addButtonHandlerFn={getLogs}>
        <div className="responsive-table">
          <table className="custome-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Date</th>
                <th>Recon Status</th>
                <th>Error Report</th>
                <th>Report</th>
                <th>View Report</th>
                {/* <th>Selective Invoice</th> */}
              </tr>
            </thead>
            <tbody>
              {tableData?.map((value) => {
                const isFailed = value.recon_status === 'Fail'; // changed: exact match only
                const isSuccess = value.recon_status === 'Pass';
                const canDownloadReport = isSuccess || isFailed;
                return (
                  <tr key={value.log_id}>
                    <td>{value.period}</td>
                    <td>{value.date}</td>
                    <td>{value.recon_status}</td>
                    <td>
                      {downloadingErrorId === value.log_id ? (
                        <CircularProgress size={14} thickness={5} sx={{ color: "#63acf7" }} />
                      ) : (
                        <DownloadIcon
                          style={{
                            color: isFailed ? "#63acf7" : "#B4B2A9",
                            cursor: isFailed ? "pointer" : "not-allowed",
                            opacity: isFailed ? 1 : 0.5,
                            pointerEvents: isFailed ? "auto" : "none",
                          }}
                          onClick={async () => {
                            setDownloadingErrorId(value.log_id);
                            await downloadErrorExcel(value.log_id);
                            setDownloadingErrorId(null);
                          }}
                        />
                      )}
                      {/* <DownloadIcon
                        style={{
                          color: isFailed ? "#63acf7" : "#B4B2A9",
                          cursor: isFailed ? "pointer" : "not-allowed",
                          opacity: isFailed ? 1 : 0.5,
                          pointerEvents: isFailed ? "auto" : "none",
                        }}
                        onClick={() => {
                          downloadErrorExcel(value.log_id)
                         
                        }} */}

                    </td>
                    <td>
                      {downloadingReportId === value.current_month_gstr6_recon_id ? (
                        <CircularProgress size={14} thickness={5} sx={{ color: "#63acf7" }} />
                      ) : (
                        <DownloadIcon
                          style={{
                            color: canDownloadReport ? "#63acf7" : "#B4B2A9",
                            cursor: canDownloadReport ? "pointer" : "not-allowed",
                            opacity: canDownloadReport ? 1 : 0.5,
                            pointerEvents: canDownloadReport ? "auto" : "none",
                          }}
                          onClick={async () => {
                            if (!canDownloadReport) return;
                            setDownloadingReportId(value.current_month_gstr6_recon_id);
                            await downloadExcel(value.current_month_gstr6_recon_id);
                            setDownloadingReportId(null);
                          }}
                        />
                      )}
                      {/* <DownloadIcon
                        style={{
                          color: canDownloadReport ? "#63acf7" : "#B4B2A9",
                          cursor: canDownloadReport ? "pointer" : "not-allowed",
                          opacity: canDownloadReport ? 1 : 0.5,
                          pointerEvents: canDownloadReport ? "auto" : "none",
                        }}
                        onClick={() => {
                          if (!canDownloadReport) return;
                          downloadExcel(value.current_month_gstr6_recon_id)
                        }}
                      /> */}
                    </td>
                    {/* <td>
                      <DownloadIcon
                        style={{
                          color: isSuccess ? "#63acf7" : "#B4B2A9",
                          cursor: isSuccess ? "pointer" : "not-allowed",
                          opacity: isSuccess ? 1 : 0.5,
                          pointerEvents: isSuccess ? "auto" : "none",
                        }}
                        onClick={() => {
                          downloadExcel(value.current_month_gstr6_recon_id)
                          // your download/error-report logic here
                        }}
                      />
                    </td> */}
                    <td>
                      <VisibilityIcon
                        style={{
                          color: isSuccess ? "#63acf7" : "#B4B2A9",
                          cursor: isSuccess ? "pointer" : "not-allowed",
                          opacity: isSuccess ? 1 : 0.5,
                          pointerEvents: isSuccess ? "auto" : "none",
                        }}
                        onClick={() => {
                          handleClick(value.current_month_gstr6_recon_id);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
              {tableData.length <= 0 && (
                <tr>
                  <td style={{ color: "red", textAlign: "center" }} colSpan={3}>No Data Found!!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </BoxWrapper>

      {/* <GSTR6FilingReport
        reconId={reconId} loading={loading} filingOutput={filingOutput} openDragDrop={openDragDrop}
        isISDUploaded={isISDUploaded} setIsISDUploaded={setIsISDUploaded} setOpenDragDrop={setOpenDragDrop}
        setFilingOutput={setFilingOutput} rows={rows} setRows={setRows} rowCount={rowCount} page={page}
        setPage={setPage} sortModel={sortModel} setSortModel={setSortModel} filterModel={filterModel}
        setFilterModel={setFilterModel} fetchDataGridData={fetchDataGridData} reportPeriod={reportPeriod}
        onReset={handleReset} downloadExcel={downloadExcel} handleFileUpload={handleFileUpload} uploadFile={uploadFile}
      /> */}

      {/* ── Coloured Recon Popup ── */}
      <ReconPopupDialog
        open={reconPopup}
        onClose={() => setReconPopup(false)}
        data={reconPopupData}
        rowCount={rowCount}
        onSubmit={handleReconPopupSubmit}
        reconId={reconPopupReconId}
        onFilesAdded={handleFilesAdded}
      />


      <Dialog open={uploadDialog} onClose={handleCloseUpload} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "0.5px solid #D8D4C9" }}>
          ISD Data Upload
          <IconButton onClick={handleCloseUpload} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", height: "100%", minHeight: 380 }}>

            {/* ── LEFT: Upload Zone ── */}
            <Box sx={{
              width: "38%",
              borderRight: "1px solid #EEEBE4",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              background: "linear-gradient(160deg, #F4F8FF 0%, #FAF8FF 100%)",
              gap: 1.5,
            }}>
              <Typography variant="caption" sx={{
                color: "#9B9890", letterSpacing: 0.8, fontWeight: 700,
                textTransform: "uppercase", fontSize: 10, mb: 1,
              }}>
                Upload ISD Data
              </Typography>

              <GenericFileUpload onFilesAdded={handleIsdFilesAdded} />

              {uploadedFiles.length > 0 && (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  background: "#EEF4FF", borderRadius: 2, px: 1.5, py: 0.8, mt: 1,
                }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#4D8EF7" }} />
                  <Typography sx={{ fontSize: 11.5, color: "#4D8EF7", fontWeight: 600 }}>
                    {uploadedFiles.length} file{uploadedFiles.length > 1 ? "s" : ""} selected
                  </Typography>
                </Box>
              )}

              {/* <Button
          onClick={handleClick}
          variant="contained"
          size="small"
          sx={{
            mt: 1,
            fontSize: 12, fontWeight: 600, borderRadius: 2,
            background: "linear-gradient(135deg, #4D8EF7 0%, #7C5CF5 100%)",
            boxShadow: "0 2px 8px rgba(77,142,247,0.35)",
            textTransform: "none", px: 3, py: 0.9,
            "&:hover": { background: "linear-gradient(135deg, #3A7EE8 0%, #6A4AE0 100%)" },
          }}
        >
          Upload
        </Button> */}
            </Box>

            {/* ── RIGHT: Logs Table ── */}
            {/* ── RIGHT: Logs Table ── */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="caption" sx={{
                  color: "#9B9890", letterSpacing: 0.8, fontWeight: 700,
                  textTransform: "uppercase", fontSize: 10,
                }}>
                  ISD Upload Logs
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={
                      downloadingIsdTemplate
                        ? <CircularProgress size={12} thickness={5} sx={{ color: "#5C7FA8" }} />
                        : <DownloadIcon style={{ fontSize: 13 }} />
                    }
                    onClick={() => downloadExcelISDTemplate(reconId)}
                    disabled={downloadingIsdTemplate}
                    sx={{
                      fontSize: 10.5, fontWeight: 600, textTransform: "none",
                      borderRadius: 2, px: 1.5, py: 0.4,
                      color: "#5C7FA8",
                      border: "1px solid #C5D8FB",
                      background: "#F4F8FF",
                      "&:hover": { background: "#EEF4FF", borderColor: "#4D8EF7" },
                      "&.Mui-disabled": { opacity: 0.6 },
                    }}
                  >
                    {downloadingIsdTemplate ? "Downloading..." : "ISD Template"}
                  </Button>
                  {/* <Button
                    size="small"
                    startIcon={<DownloadIcon style={{ fontSize: 13 }} />}
                    onClick={() => downloadExcelISDTemplate(reconId)}
                    sx={{
                      fontSize: 10.5, fontWeight: 600, textTransform: "none",
                      borderRadius: 2, px: 1.5, py: 0.4,
                      color: "#5C7FA8",
                      border: "1px solid #C5D8FB",
                      background: "#F4F8FF",
                      "&:hover": { background: "#EEF4FF", borderColor: "#4D8EF7" },
                    }}
                  >
                    ISD Template
                  </Button> */}
                  {/* ── Refresh Button ── */}
                  <Button
                    size="small"
                    startIcon={<RefreshIcon style={{ fontSize: 13 }} />}
                    onClick={getUploadLogs}
                    sx={{
                      fontSize: 10.5, fontWeight: 600, textTransform: "none",
                      borderRadius: 2, px: 1.5, py: 0.4,
                      color: "#5C7FA8",
                      border: "1px solid #C5D8FB",
                      background: "#F4F8FF",
                      "&:hover": { background: "#EEF4FF", borderColor: "#4D8EF7" },
                    }}
                  >
                    Refresh
                  </Button>

                  {/* ── Download Report Button ── */}
                  {/* <Button
                    size="small"
                    startIcon={<DownloadIcon style={{ fontSize: 13 }} />}
                    onClick={() => {
                      const latestLog = uploadLogs.find(log => log.status === 'Pass');
                      if (!latestLog?.recon_id) {
                        toast.error("No successful upload found to download report");
                        return;
                      }
                      downloadUploadReport(latestLog.recon_id);
                    }}
                    disabled={!uploadLogs.some(log => log.status === 'Pass')}
                    sx={{
                      fontSize: 10.5, fontWeight: 600, textTransform: "none",
                      borderRadius: 2, px: 1.5, py: 0.4,
                      color: "#5C7FA8",
                      border: "1px solid #C5D8FB",
                      background: "#F4F8FF",
                      "&:hover": { background: "#EEF4FF", borderColor: "#4D8EF7" },
                      "&.Mui-disabled": { opacity: 0.5, cursor: "not-allowed" },
                    }}
                  >
                    Download Report
                  </Button> */}
                </Box>
              </Box>

              <Box sx={{ overflow: "auto", borderRadius: 2, border: "1px solid #E8E4DC", maxHeight: 260 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                  <thead>
                    <tr style={{ background: "#F5F2EC", position: "sticky", top: 0, zIndex: 1 }}>
                      {["Period", "Start Time", "Status", "Error"].map((col) => (
                        <th key={col} style={{
                          padding: "7px 10px", textAlign: "left", fontWeight: 700,
                          fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase",
                          color: "#8C8880", borderBottom: "1px solid #E8E4DC", whiteSpace: "nowrap",
                        }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadLogs?.map((value, index) => {
                      const isFailed = value.status === 'Fail' || value.status === 'Failed';
                      const isSuccess = value.status === 'Pass';


                      return (
                        <tr key={value.log_id}
                          style={{ background: index % 2 === 0 ? "#FDFCFA" : "#F9F7F4" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F2EFE8"}
                          onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? "#FDFCFA" : "#F9F7F4"}
                        >
                          <td style={{ padding: "6px 10px", color: "#3D3A34", fontWeight: 600, borderBottom: "1px solid #EFECE6" }}>{value.period}</td>
                          <td style={{ padding: "6px 10px", color: "#6B6860", borderBottom: "1px solid #EFECE6", whiteSpace: "nowrap" }}>{value.start_time}</td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #EFECE6" }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700,
                              background: isSuccess ? "#EDFAF1" : isFailed ? "#FEF0EE" : "#F2F0EC",
                              color: isSuccess ? "#276B42" : isFailed ? "#A63020" : "#7A7570",
                              border: `1px solid ${isSuccess ? "#C2EDD2" : isFailed ? "#F5C8C2" : "#DDD9D2"}`,
                            }}>
                              <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSuccess ? "#276B42" : isFailed ? "#A63020" : "#9E9E9E", display: "inline-block" }} />
                              {value.status}
                            </span>
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #EFECE6" }}>
                            <DownloadIcon
                              style={{
                                fontSize: 15,
                                color: isFailed ? "#A63020" : "#C8C4BC",
                                cursor: isFailed ? "pointer" : "not-allowed",
                                opacity: isFailed ? 1 : 0.5,
                                pointerEvents: isFailed ? "auto" : "none",
                              }}
                              onClick={() => downloadErrorExcel(value.log_id)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {uploadLogs.length <= 0 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: "20px 0", color: "#B4B0A8", fontSize: 11.5 }}>
                          No upload logs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </Box>
          </Box>
          <Box sx={{
            display: "flex", justifyContent: "flex-end",
            borderTop: "0.5px solid #EEEBE4", p: 1.5,
          }}>
            <Button
              size="small"
              onClick={handleGstr6FilingSubmit}
              disabled={!isdUploadSuccess || gstr6Filing}
              startIcon={gstr6Filing && <CircularProgress size={12} thickness={5} sx={{ color: "#5C4DB8" }} />}
              sx={{
                fontSize: 11.5, fontWeight: 600, textTransform: "none",
                borderRadius: 2, px: 2, py: 0.6,
                color: "#5C4DB8",
                border: "1px solid #D9D2FB",
                background: "#F5F2FF",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #4D8EF7 0%, #7C5CF5 100%)",
                  color: "#fff",
                  borderColor: "transparent",
                  boxShadow: "0 2px 8px rgba(124,92,245,0.35)",
                },
                "&.Mui-disabled": { opacity: 0.5, cursor: "not-allowed" },
              }}
            >
              {gstr6Filing ? "Filing..." : "GSTR 6 File"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── GSTR 6 Coming Soon Dialog ── */}
      <Dialog
        open={gstr6Dialog}
        onClose={() => setGstr6Dialog(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 320 } }}
      >
        <DialogContent sx={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", textAlign: "center", py: 5, px: 4,
          background: "linear-gradient(160deg, #F7F9FF 0%, #FAF8FF 100%)",
        }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "50%",
            background: "linear-gradient(135deg, #EAF0FF 0%, #F1EBFF 100%)",
            border: "1px solid #D9E3FB",
            display: "flex", alignItems: "center", justifyContent: "center",
            mb: 2,
          }}>
            <ScheduleIcon sx={{ fontSize: 22, color: "#7C5CF5" }} />
          </Box>

          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#2E2C28", mb: 0.5 }}>
            Coming Soon
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#8C8880", mb: 3 }}>
            GSTR 6 filing will be available shortly.
          </Typography>

          <Button
            size="small"
            onClick={() => setGstr6Dialog(false)}
            sx={{
              fontSize: 11.5, fontWeight: 600, textTransform: "none",
              borderRadius: 2, px: 3, py: 0.6,
              color: "#fff",
              background: "linear-gradient(135deg, #4D8EF7 0%, #7C5CF5 100%)",
              boxShadow: "0 2px 8px rgba(77,142,247,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #3A7EE8 0%, #6A4AE0 100%)" },
            }}
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>


      <Dialog
        open={isProcessing}
        disableEscapeKeyDown
        onClose={() => { }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: 320,
            boxShadow: "0 8px 32px rgba(83, 74, 183, 0.18)",
          },
        }}
      >
        <Box sx={{ p: 3.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>

          {/* Animated icon */}
          <Box
            sx={{
              width: 56, height: 56, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #EEEDFE 0%, #F4F2FE 100%)",
              position: "relative",
            }}
          >
            <CircularProgress
              size={56}
              thickness={3}
              sx={{
                color: "#534AB7",
                position: "absolute",
                top: 0, left: 0,
              }}
            />
            <Typography sx={{ fontSize: 18 }}>📊</Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 14.5, color: "#2B2A27" }}>
              Running GSTR-6 Reconciliation
            </Typography>
            <Typography variant="caption" sx={{ color: "#8C8880", fontSize: 11.5, mt: 0.3, display: "block" }}>
              {processingMessage || "Please wait..."}
            </Typography>
          </Box>

          {/* Step indicator - compact pill style */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", mt: 0.5 }}>
            {["Connecting", "Reconciling", "Fetching"].map((label, i) => (
              <Box key={label} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6 }}>
                <Box
                  sx={{
                    width: "100%", height: 4, borderRadius: 4,
                    background: i <= processingStep ? "#534AB7" : "#EBE9F5",
                    transition: "background 0.4s ease",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 9.5, fontWeight: i === processingStep ? 700 : 500,
                    color: i <= processingStep ? "#534AB7" : "#B4B0C4",
                    letterSpacing: 0.2,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Dialog>


      {/* ── ISD Branch Dialog ── */}
      <Dialog open={isdDialog} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          ISD Branch
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {newIsdItems.map((isd, i) => (
            <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
              <TextField label="Branch Name" value={isd.branch_name ?? ""} onChange={handleIsdFormFieldChange("branch_name", i)} InputProps={{ readOnly: isdReadOnly }} fullWidth />
              <TextField label="GSTIN" value={isd.gst ?? ""} onChange={handleIsdFormFieldChange("gst", i)} InputProps={{ readOnly: isdReadOnly }} fullWidth />
              <TextField label="Turnover" value={isd.turnover ?? ""} onChange={handleIsdFormFieldChange("turnover", i)} InputProps={{ readOnly: isdReadOnly }} fullWidth />
              {!isdReadOnly && i === newIsdItems.length - 1 && (
                <IconButton onClick={handleAddRow} color="primary" sx={{ "&:hover": { backgroundColor: "transparent" } }}>
                  <AddCircle />
                </IconButton>
              )}
            </Box>
          ))}
          <Grid container justifyContent="center" style={{ marginTop: "1rem" }}>
            {isdReadOnly ? (
              <Grid item>
                <button className="button-primary" type="button" onClick={() => setIsdReadOnly(false)}>Edit</button>
              </Grid>
            ) : (
              <>
                <Grid item>
                  <button className="button-primary" type="button" onClick={updateISDItems}>Submit</button>
                </Grid>
                <Grid item>
                  <button className="button-outlined-secondary" type="button" onClick={() => { setIsdReadOnly(true); setNewIsdItems(isdBranchDetails); }}>Cancel</button>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* ── Establish Connection Dialog ── */}
      <Dialog open={openEstablisConnectionDrawer} onClose={closeEstablisConnectionDrawerHamdler} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 24, p: 2, bgcolor: "background.default" } }}>
        <DialogTitle style={{ fontWeight: "600" }}>Establish Connection</DialogTitle>
        <DialogContent>
          <ValidatorForm ref={form} onSubmit={getOtpForAuthHandler} onError={(errors) => console.log(errors)}>
            <Inputfield textLabel="User Name" inputType="text" name="user_name" value={username} textOnchangeFunction={(e) => setUserName(e.target.value)} requiredField="*" validator={["required"]} errorMsg={["This field is required"]} />
            <div className="mt-20">
              <button className="button-primary" type="submit">Submit</button>
              <button className="button-outlined-secondary" type="button" onClick={closeEstablisConnectionDrawerHamdler}>Close</button>
            </div>
          </ValidatorForm>
        </DialogContent>
      </Dialog>

      {/* ── OTP Modal ── */}
      <Modal open={openOTPModal}>
        <Box sx={style}>
          <ValidatorForm ref={form} onSubmit={vyanaAuthHandler} onError={(errors) => console.log("ValidatorForm errors:", errors)}>
            <Inputfield textLabel="OTP" inputType="text" name="OTP" value={auth_otp} textOnchangeFunction={(e) => setAuthOTP(e.target.value)} requiredField="*" validator={["required"]} errorMsg={["This field is required"]} />
            <div className="mt-20">
              <button className="button-primary" type="submit">Submit</button>
              <button className="button-outlined-secondary" type="button" onClick={closeOptModalHandler}>Close</button>
            </div>
          </ValidatorForm>
        </Box>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}